import {
  CustomFieldsDateRangeValue,
  DiscountDateRangeValue,
  FuelSurchargeDateRangeValue,
  PeakSurchargeDateRangeValue,
} from "../../../pages/clients/invoices/ClientInvoiceInterfaces";
import {
  RateCardCustomFieldsResponse,
  RateCardFuelSurchargeFieldsResponse,
  RateCardPeakSurchargeResponse,
} from "../../../pages/clients/ratecard/dtos/RateCardResponse";
import { RateCardCustomFields, SimpleValueDateRangeType } from "../requestModels/ClientInvoiceEditRequest";

export interface RatecardClientInvoiceResponse {
  id: number;
  clientInvoiceId: string;
  clientId: number;
  stores: StoreResponseForClientInvoice[];
  invoiceDate: string;
  invoiceStatus: string;
  startDateRange: string;
  endDateRange: string;
  isAutoGenerated: boolean;
  isEdited: boolean;
  invoiceFilePath: string;
  submitDate: string;
  editable: boolean;
  approvable: boolean;
  flagable: boolean;
  currentStatus: InvoiceStatus;
  createdOn: string;
  updatedOn: string;
  createdBy: string;
  updatedBy: string;
  fdpCompanyNames: string[];
  isSoftdelete: boolean,
  fdcTotalNetPayment: number;
}

export interface InvoiceStatus {
  id: number;
  internalStatus: string;
  externalStatus: string;
  clientStatus: boolean;
  fdpStatus: boolean;
  active: boolean;
}

export interface RouteResponse {
  id: number;
  date: string;
  tripId: string;
  driverUserId: string;
  totalOrders: number;
  ordersDelivered: number;
  ordersUndelivered: number;
  ordersUndeliveredNoAttempt: number;
  ordersUndeliveredAttempt: number;
  updatedOn: string;
  createdOn: string;
  createdBy: string;
  updatedBy: string;
}

export interface ClientInvoiceEditResponse {
  id: number;
  storeId: number;
  clientInvoiceId: string;
  clientId?: number;
  editStartRange: string;
  editEndRange: string;
  editRoute: CiRouteEditReponse;
  isRatecardEdited: boolean;
  clientName?: string;
}

export interface CiRouteEditReponse {
  routeId: number;
  tripId: string;
  totalOrders: number;
  ordersDelivered: number;
  ordersUndelivered: number;
  ordersUndeliveredNoAttempt: number;
}

export interface ClientInvoiceEditDetailResponse {
  id: number;
  storeId: number;
  editStartRange: string;
  editEndRange: string;
  totalLateCancellation: number;
  onetimeAdjustmentValue: number;
  onetimeAdjustmentDescription: string;
  editRoute: CiRouteEditReponse;
  discountCustomValue: number;
  fuelCustomValue: number | '';
  peakCustomValue: number;
  ratecardCustomFieldsDto: [];
  ratecardValues: CiRatecardValueResponse;
  ratecardFuelSurchargeFields: RateCardFuelSurchargeFieldsResponse[];
  ratecardPeakSurcharges: RateCardPeakSurchargeResponse[];
}

export interface TripAndDateRangeDataForSelectedStores {
  storeId: number;
  route: RouteResponse;
  startDateRange: string;
  endDateRange: string;
  date: string;
}

export interface StoreResponseForClientInvoice {
  id: number;
  storeId: string;
  ratecardId: number;
}

export interface CiRatecardValueResponse {
  id: number;
  baseRate: number;
  variableRate: number;
  variableRateType: string;
  routeMinimum: number;
  lateCancellationCharge: number;
  lateCancellationType: string;
  fuelType: string;
  regionName: string;
  discountType: string;
  discountMinVolumeRate: number;
  discountFrequency: number;
  discountUnitType: string;
  discountValue: number;
  createdOn: string;
  updatedOn: string;
}

export interface ClientInvoiceCommentResponse {
  invoicePkId: number;
  invoiceId: string;
  commentDetails: InvoiceComment[];
}

export interface InvoiceComment {
  id: number;
  comment: string;
  replyComment: string;
  status: string;
  author: string;
  replier: string;
  commentEditable: boolean;
  commentDeletable: boolean;
  commentRepliable: boolean;
  commentReplyEditable: boolean;
  createdOn: string;
  updatedOn: string;
}

export interface FdpInvoiceCommentResponse {
  invoicePkId: number;
  invoiceId: string;
  commentDetails: FdpInvoiceComment[];
}

export interface FdpInvoiceComment {
  id: number;
  comment: string;
  replyComment: string;
  status: string;
  author: string;
  replier: string;
  commentEditable: boolean;
  commentDeletable: boolean;
  commentRepliable: boolean;
  commentReplyEditable: boolean;
  createdOn: string;
  updatedOn: string;
}

export interface DateRangeInvoiceEditsResponse {
  fieldEdits: SimpleValueDateRangeType[];
  discountDateRangeEdits: DiscountDateRangeValue[];
  fuelSurchargeDateRangeEdits: FuelSurchargeDateRangeValue[];
  customFieldsDateRangeEdits: CustomFieldsDateRangeValue[];
  peakSurchargeDateRangeEdits: PeakSurchargeDateRangeValue[];
}

export interface ClientInvoiceLce {
  id?: number;
  clientInvoiceId?: number;
  totalLateCancels: any;
  fdpId: number;
  fdpName: string;
  operationDate: string | undefined;
  storePkId: number;
  storeId: string;
}

export interface ClientInvoiceOta {
  id?: number;
  clientInvoiceId?: number;
  fdpInvoiceId?: number;
  fdpId: number;
  fdpName: string;
  operationDate?: string;
  otaValue: string;
  operator: string;
  otaDescription?: string;
  adjustmentType: AdjustmentType;
  nextAdjustmentOn: AdjustmentType;
}

export enum AdjustmentType {
  FDP_INVOICE = "fdp_invoice",
  CLIENT_INVOICE = "client_invoice",
}

export interface SubmittedClientInvoice {
  id: number;
  clientInvoiceId: string;
  clientInvoiceAutoId: number;
  status: string;
  clientId: number;
  associatedStores: string;
  startDate: string;
  endDate: string;
  submitDate: string;
  associatedFdps: string;
  netPayment: number;
  manualUpload: boolean;
  createdOn: string;
  updatedOn: string;
}

export interface ClientInvoiceWithDateRange {
  id: number;
  clientInvoiceId: string;
  clientId: number;
  startDateRange: string;
  endDateRange: string;
}
